import { useState, useEffect, useCallback } from 'react';
import { X, Loader2, Mail } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from './ui/input-otp';
import { Button } from './ui/button';

interface OtpVerificationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: string;
  purpose: string;
  onVerified: (code: string) => void;
  onResend: () => Promise<void>;
  isVerifying?: boolean;
  error?: string | null;
}

export function OtpVerificationModal({
  open,
  onOpenChange,
  email,
  purpose,
  onVerified,
  onResend,
  isVerifying = false,
  error = null,
}: OtpVerificationModalProps) {
  const [code, setCode] = useState('');
  const [resendCountdown, setResendCountdown] = useState(60);
  const [isResending, setIsResending] = useState(false);

  // Start countdown on open
  useEffect(() => {
    if (!open) {
      setCode('');
      setResendCountdown(60);
      return;
    }

    const timer = setInterval(() => {
      setResendCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [open]);

  const handleComplete = useCallback(
    (value: string) => {
      if (value.length === 6) {
        onVerified(value);
      }
    },
    [onVerified],
  );

  const handleResend = async () => {
    setIsResending(true);
    try {
      await onResend();
      setResendCountdown(60);
      setCode('');
    } catch {
      // Error handled by parent
    } finally {
      setIsResending(false);
    }
  };

  // Mask email: show first 2 chars + ... + domain
  const maskedEmail = (() => {
    const [local, domain] = email.split('@');
    if (!domain) return email;
    const visible = local.slice(0, 2);
    return `${visible}***@${domain}`;
  })();

  const purposeLabels: Record<string, string> = {
    registration: 'Registration',
    email_update: 'Email Update',
    password_reset: 'Password Reset',
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[2000] flex items-end sm:items-center justify-center">
      <div className="bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md max-h-[90vh] overflow-auto pb-[env(safe-area-inset-bottom)]">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 rounded-t-3xl">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Verify Email</h2>
            <button
              onClick={() => onOpenChange(false)}
              disabled={isVerifying}
              className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 transition-all disabled:opacity-50"
            >
              <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 flex flex-col items-center">
          {/* Email Icon */}
          <div className="w-16 h-16 rounded-full bg-[#8bc43e]/10 flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-[#8bc43e]" />
          </div>

          <p className="text-center text-gray-600 dark:text-gray-300 mb-2">
            We sent a 6-digit code to
          </p>
          <p className="text-center font-semibold text-gray-900 dark:text-white mb-6">
            {maskedEmail}
          </p>

          {/* OTP Input */}
          <div className="mb-6">
            <InputOTP
              maxLength={6}
              value={code}
              onChange={(value) => {
                setCode(value);
                if (value.length === 6) {
                  handleComplete(value);
                }
              }}
              disabled={isVerifying}
            >
              <InputOTPGroup className="gap-2">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <InputOTPSlot
                    key={index}
                    index={index}
                    className="w-12 h-14 text-lg font-semibold border-2 rounded-xl"
                  />
                ))}
              </InputOTPGroup>
            </InputOTP>
          </div>

          {/* Error */}
          {error && (
            <div className="w-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3 mb-4">
              <p className="text-sm text-red-600 dark:text-red-400 text-center">{error}</p>
            </div>
          )}

          {/* Verify Button */}
          <Button
            onClick={() => handleComplete(code)}
            disabled={code.length !== 6 || isVerifying}
            className="w-full h-14 bg-[#8bc43e] hover:bg-[#7ab32e] text-white rounded-xl font-semibold disabled:opacity-50 mb-4"
          >
            {isVerifying ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Verifying...</span>
              </div>
            ) : (
              'Verify'
            )}
          </Button>

          {/* Resend */}
          <div className="text-center">
            {resendCountdown > 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Resend code in <span className="font-semibold text-gray-900 dark:text-white">{resendCountdown}s</span>
              </p>
            ) : (
              <button
                onClick={handleResend}
                disabled={isResending}
                className="text-sm text-[#8bc43e] font-semibold hover:underline disabled:opacity-50"
              >
                {isResending ? 'Sending...' : 'Resend Code'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
